import { useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Phone, Sprout, Stethoscope, ShieldAlert,
  Mic, Cpu, BarChart3, Fish, Leaf, Wifi,
  Brain, Radar, Globe, Shield, Activity,
  CloudRain, TrendingUp, Heart, Radio,
  Crosshair, Lock, Satellite,
} from "lucide-react";

const verticals = [
  {
    id: "vani",
    name: "OJAS VANI",
    meaning: "Sacred Voice / Intelligent Speech",
    tagline: "Voice-first enterprise AI platform",
    status: "Live",
    icon: Phone,
    image: "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?q=80&w=800&auto=format&fit=crop",
    story: "In India's rapidly evolving BFSI landscape, millions of loan servicing interactions happen daily across languages, dialects, and cultural contexts. Traditional contact centers crumble under the weight of scale, linguistic diversity, and ever-tightening regulatory requirements. Ojas Vani was born from a singular vision — that AI should speak the language of India's people, literally. Our conversational AI platform doesn't just automate calls; it understands context, negotiates with empathy, and maintains unwavering compliance across every interaction.",
    vision: "Transforming every customer interaction into an intelligent, compliant, and deeply human conversation — at unprecedented scale.",
    capabilities: [
      { icon: Mic, title: "Voice AI for BFSI", desc: "Automated repayment reminders, loan servicing, EMI negotiations, KYC verification, and PTP confirmations — handling millions of calls with human-like understanding." },
      { icon: Cpu, title: "LLM Orchestration", desc: "Governance-layered large language model system with finite state machine and agentic workflows for complex, multi-turn conversations that adapt in real-time." },
      { icon: Phone, title: "Real-time Telephony", desc: "Native integration with Exotel, Twilio, and SIP protocols. Sub-4 second latency with intelligent barge-in detection and natural turn-taking." },
      { icon: Globe, title: "Multilingual Intelligence", desc: "Seamless code-switching between Hindi, English, and Hinglish with regional dialect understanding and deep cultural context awareness." },
      { icon: BarChart3, title: "Analytics & Insights", desc: "Real-time call sentiment analysis, recovery performance dashboards, compliance audit reports, and agent productivity metrics that drive decisions." },
      { icon: Shield, title: "Compliance Engine", desc: "Built-in RBI guidelines alignment, DPDP Act compliance, end-to-end encryption, immutable audit logs, and automated consent management." },
    ],
  },
  {
    id: "krishi",
    name: "OJAS KRISHI",
    meaning: "Energy of Agriculture / Vitality of Farming",
    tagline: "AI-powered smart agriculture ecosystem",
    status: "Live",
    icon: Sprout,
    image: "https://images.pexels.com/photos/29858623/pexels-photo-29858623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    story: "India feeds 1.4 billion people, yet its farmers often work without access to the intelligent tools that could transform their livelihoods. Climate unpredictability, water scarcity, and market volatility threaten the very foundation of our food security. Ojas Krishi was conceived to bridge this critical gap — combining artificial intelligence with IoT infrastructure to create an ecosystem where data-driven decisions replace centuries of guesswork. From the fish ponds of Bihar to the wheat fields of Punjab, we're building technology that speaks the farmer's language.",
    vision: "Empowering every farmer, fishery, and agri-enterprise with intelligent automation that increases yield, reduces waste, and builds climate resilience.",
    capabilities: [
      { icon: Fish, title: "Smart Aquaculture", desc: "AI-powered fish feeders with computer vision for disease detection, automated water quality monitoring via IoT sensors, and feeding optimization that reduces waste by up to 40%." },
      { icon: Leaf, title: "Smart Farming", desc: "Comprehensive soil health monitoring, AI-driven irrigation recommendations, and mobile-first crop disease detection using computer vision that works even in low-connectivity areas." },
      { icon: Wifi, title: "IoT Infrastructure", desc: "Purpose-built low-power rural sensor networks with edge AI for offline decision-making, connected to cloud dashboards that give agri-enterprises real-time visibility." },
      { icon: TrendingUp, title: "Yield Prediction", desc: "Machine learning models that forecast crop yields by analyzing soil composition, weather patterns, historical data, and satellite imagery — helping farmers plan smarter." },
      { icon: CloudRain, title: "Climate Resilience", desc: "Advanced weather pattern analysis combined with adaptive farming recommendations that help farmers navigate increasingly unpredictable climate conditions." },
      { icon: BarChart3, title: "Market Intelligence", desc: "Real-time commodity pricing, demand forecasting, and optimal harvest timing suggestions that help farmers maximize returns on their produce." },
    ],
  },
  {
    id: "arogya",
    name: "OJAS AROGYA",
    meaning: "Health, Well-being & Vitality",
    tagline: "India-first AI healthcare ecosystem",
    status: "Coming Soon",
    icon: Stethoscope,
    image: "https://images.unsplash.com/photo-1758691462620-9018c602ed3e?q=80&w=800&auto=format&fit=crop",
    story: "With one of the lowest doctor-to-patient ratios in the world, India's healthcare system faces an unprecedented challenge of scale and accessibility. Rural communities often travel hours for basic medical consultation, while urban hospitals burst at the seams. Ojas Arogya envisions an India where intelligent AI systems augment healthcare delivery — making quality medical guidance accessible to every citizen regardless of geography. We're building Make-in-India healthcare AI that respects patient privacy while amplifying the reach and capability of every healthcare professional.",
    vision: "Democratizing quality healthcare across India through AI that augments doctors, empowers patients, and bridges the gap between urban expertise and rural need.",
    capabilities: [
      { icon: Brain, title: "Clinical Decision Support", desc: "AI-powered diagnostic assistance that helps clinicians make faster, more accurate decisions by analyzing symptoms, medical history, and latest research — designed as a co-pilot, never a replacement." },
      { icon: Mic, title: "Voice-enabled Triage", desc: "Intelligent voice-based patient intake and triage system that can assess urgency, gather medical history, and route patients to appropriate care — in multiple Indian languages." },
      { icon: Activity, title: "Remote Monitoring", desc: "Continuous AI-powered health monitoring with smart alerts for early intervention, particularly designed for chronic disease management in underserved communities." },
      { icon: Heart, title: "Patient Care AI", desc: "Medical document summarization, treatment plan optimization, and patient communication tools that reduce administrative burden and let doctors focus on healing." },
      { icon: Radio, title: "Telemedicine AI", desc: "AI-enhanced rural telemedicine platform that bridges the gap between specialist knowledge in cities and primary care needs in villages across India." },
      { icon: Lock, title: "Health Data Privacy", desc: "DPDP-compliant health data management with patient consent management, data anonymization, and sovereign storage — because health data is the most sensitive of all." },
    ],
  },
  {
    id: "raksha",
    name: "OJAS RAKSHA",
    meaning: "Protection, Defense & Safeguarding",
    tagline: "Sovereign AI for national security",
    status: "Coming Soon",
    icon: ShieldAlert,
    image: "https://images.unsplash.com/photo-1729183611610-e29c1fddf0a9?q=80&w=800&auto=format&fit=crop",
    story: "In an era where artificial intelligence capabilities increasingly define national strategic advantage, depending on foreign AI systems for defense creates a fundamental vulnerability. The intelligence that guards a nation must be built, controlled, and trusted by that nation alone. Ojas Raksha represents our deepest commitment — building AI systems that India owns, controls, and trusts for its most critical security needs. From edge computing in tactical environments to autonomous threat detection across vast borders, we're building the sovereign AI backbone for India's defense apparatus.",
    vision: "Ensuring India's strategic autonomy through sovereign, trustworthy AI systems that protect the nation while respecting constitutional values.",
    capabilities: [
      { icon: Crosshair, title: "Threat Detection", desc: "Autonomous multi-sensor threat detection and classification systems that operate in real-time across diverse environments — from urban landscapes to remote border areas." },
      { icon: Cpu, title: "Edge AI Tactical", desc: "Ruggedized edge computing systems designed for tactical environments where cloud connectivity is unreliable — making critical decisions locally with military-grade reliability." },
      { icon: Satellite, title: "Surveillance Analytics", desc: "Privacy-preserving surveillance analytics that can process vast amounts of visual and signal data while maintaining constitutional safeguards and accountability." },
      { icon: Brain, title: "Decision Support", desc: "AI-powered strategic and tactical decision support systems that augment human judgment with real-time data analysis, scenario modeling, and risk assessment." },
      { icon: Mic, title: "Mission-critical Voice", desc: "Secure voice AI systems for command and control applications, encrypted communications analysis, and multi-language intelligence processing." },
      { icon: Shield, title: "Cyber Defense", desc: "AI-driven cybersecurity systems for critical infrastructure protection, threat hunting, anomaly detection, and automated incident response at national scale." },
    ],
  },
];

export const VerticalsSection = () => {
  const [active, setActive] = useState("vani");
  const current = verticals.find((v) => v.id === active);

  return (
    <section id="solutions" data-testid="verticals-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-[#c9a84c] font-heading font-semibold text-sm tracking-widest uppercase mb-4">
            Business Verticals
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight">
            Four Pillars of <span className="text-gradient-gold">Innovation</span>
          </h2>
          <p className="font-body text-muted-foreground mt-4 max-w-3xl text-base leading-relaxed">
            Each vertical represents a mission-critical domain where sovereign AI
            can transform outcomes. From voice intelligence reshaping financial
            services to smart agriculture feeding a nation — every solution carries
            a story of impact.
          </p>
        </motion.div>

        <Tabs value={active} onValueChange={setActive} className="mt-12">
          <TabsList className="bg-white dark:bg-[#0a1628]/80 border border-border dark:border-[#c9a84c]/20 p-1.5 h-auto flex flex-wrap gap-1.5 w-full justify-start shadow-sm dark:shadow-none">
            {verticals.map((v) => (
              <TabsTrigger
                key={v.id}
                value={v.id}
                className="font-heading text-sm px-6 py-3 data-[state=active]:bg-[#c9a84c] data-[state=active]:text-[#0a1628] data-[state=active]:font-semibold text-foreground/60 dark:text-[#94a3b8] hover:text-foreground dark:hover:text-white rounded-md transition-colors duration-200"
                data-testid={`vertical-tab-${v.id}`}
              >
                <v.icon className="h-4 w-4 mr-2" />
                {v.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {verticals.map((v) => (
            <TabsContent key={v.id} value={v.id} className="mt-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                {/* Header with image */}
                <div className="grid lg:grid-cols-5 gap-8 mb-10">
                  <div className="lg:col-span-2 relative rounded-xl overflow-hidden aspect-[4/3]">
                    <img src={v.image} alt={v.name} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <Badge
                      className={`absolute top-4 left-4 ${
                        v.status === "Live"
                          ? "bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/40"
                          : "bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/40"
                      }`}
                      variant="outline"
                      data-testid={`vertical-status-${v.id}`}
                    >
                      {v.status}
                    </Badge>
                  </div>

                  <div className="lg:col-span-3 flex flex-col justify-center">
                    <p className="text-muted-foreground font-body text-xs uppercase tracking-widest">
                      {v.meaning}
                    </p>
                    <h3 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground mt-2">
                      {v.name}
                    </h3>
                    <p className="font-heading text-[#c9a84c] text-sm mt-1 font-medium">
                      {v.tagline}
                    </p>

                    <div className="mt-6 glass-card rounded-lg p-6">
                      <p className="font-heading text-[#c9a84c] text-xs uppercase tracking-widest mb-3 font-semibold">
                        The Story
                      </p>
                      <p className="font-body text-muted-foreground text-sm leading-relaxed">
                        {v.story}
                      </p>
                    </div>

                    <div className="mt-4 p-4 rounded-lg border border-[#c9a84c]/20 bg-[#c9a84c]/5">
                      <p className="font-body text-foreground text-sm leading-relaxed italic">
                        "{v.vision}"
                      </p>
                    </div>
                  </div>
                </div>

                {/* Capabilities grid */}
                <div>
                  <h4 className="font-heading text-foreground text-lg font-semibold mb-6 uppercase tracking-wider">
                    Key Capabilities
                  </h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {v.capabilities.map((cap, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}
                        className="glass-card rounded-lg p-5 group"
                        data-testid={`capability-${v.id}-${i}`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 rounded-md bg-[#c9a84c]/10 flex items-center justify-center shrink-0 group-hover:bg-[#c9a84c]/20 transition-colors duration-300">
                            <cap.icon className="h-4.5 w-4.5 text-[#c9a84c]" />
                          </div>
                          <h5 className="font-heading font-semibold text-foreground text-sm">
                            {cap.title}
                          </h5>
                        </div>
                        <p className="font-body text-muted-foreground text-xs leading-relaxed">
                          {cap.desc}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
